// PHA-1168: the Patient List when a saved custom view is selected. It shares the
// page's search box and filters (they still narrow the list) and renders through
// ManifestTable. It reads /session/custom_views/render -- never the AI, and never
// list_with_details (the Chief-of-Staff agent's endpoint stays as it is).
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Badge, Button, Callout, Card, Flex, Text } from '@radix-ui/themes';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import {
  customViewErrorCode,
  customViewsApi,
  CustomViewResult,
  CustomViewRow,
} from '../../services/api';
import ManifestTable, { ManifestTableSort } from './ManifestTable';
import { downloadCsv, manifestToCsv, safeFileName } from './format';

export interface CustomPatientListViewHandle {
  exportCsv: () => Promise<void>;
}

interface Props {
  orgId: string;
  viewId: number;
  viewName: string;
  search: string;
  filters: Record<string, unknown>;
  pageSize: number;
  onOpenPatient: (row: CustomViewRow) => void;
  /** The view is gone or no longer compiles: the page shows Phanova Standard. */
  onUnavailable: (reason: 'not_found' | 'stale') => void;
}

const CustomPatientListView = forwardRef<CustomPatientListViewHandle, Props>(({
  orgId, viewId, viewName, search, filters, pageSize, onOpenPatient, onUnavailable,
}, ref) => {
  const [result, setResult] = useState<CustomViewResult | null>(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<ManifestTableSort | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  // Anything that changes WHICH rows are asked for restarts at page 1.
  const filterKey = JSON.stringify([orgId, viewId, search, filters, pageSize, sort]);
  const lastKey = useRef(filterKey);
  const effectivePage = lastKey.current === filterKey ? page : 1;

  useEffect(() => {
    if (lastKey.current !== filterKey) {
      lastKey.current = filterKey;
      setPage(1);
    }
  }, [filterKey]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    customViewsApi
      .render(orgId, viewId, {
        page: effectivePage, page_size: pageSize, search: search || undefined,
        filters, column_sort: sort,
      })
      .then((data) => {
        if (id !== requestId.current) return;
        if (data.stale) { onUnavailable('stale'); return; }
        setResult(data);
        setError(null);
      })
      .catch((err) => {
        if (id !== requestId.current) return;
        if (customViewErrorCode(err) === 'view_not_found') { onUnavailable('not_found'); return; }
        setError(err instanceof Error ? err.message : 'Could not load this view.');
      })
      .finally(() => { if (id === requestId.current) setLoading(false); });
    // onUnavailable is a parent callback; the request depends on the data inputs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, viewId, effectivePage, pageSize, search, JSON.stringify(filters), JSON.stringify(sort)]);

  const exportCsv = useCallback(async () => {
    const data = await customViewsApi.render(orgId, viewId, { search: search || undefined, filters, column_sort: sort, export: true });
    if (data.stale || !data.manifest) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(manifestToCsv(data.manifest, data.records), `${safeFileName(viewName)}_${stamp}.csv`);
  }, [orgId, viewId, search, filters, sort, viewName]);

  useImperativeHandle(ref, () => ({ exportCsv }), [exportCsv]);

  if (error) {
    return (
      <Callout.Root color="orange">
        <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
        <Callout.Text>{error}</Callout.Text>
      </Callout.Root>
    );
  }
  if (!result) {
    return (
      <Flex justify="center" p="6">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
      </Flex>
    );
  }

  const total = result.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (effectivePage - 1) * pageSize + 1;
  const end = Math.min(effectivePage * pageSize, total);

  return (
    <Flex direction="column" gap="3">
      <Flex align="center" gap="2">
        <Badge size="1" variant="soft" color="gray">
          {total === 0 ? '0 patients' : `Showing ${start}–${end} of ${total} patients`}
        </Badge>
        {loading && <Text size="1" color="gray">Updating…</Text>}
      </Flex>
      <Card className="shadow-sm" style={{ overflowX: 'auto' }}>
        <ManifestTable
          manifest={result.manifest}
          records={result.records}
          sort={sort}
          onSort={setSort}
          onRowClick={onOpenPatient}
        />
      </Card>
      {totalPages > 1 && (
        <Flex justify="end" align="center" gap="2">
          <Button size="1" variant="soft" disabled={effectivePage <= 1 || loading}
            onClick={() => setPage(effectivePage - 1)}>Previous</Button>
          <Text size="1" color="gray">Page {effectivePage} of {totalPages}</Text>
          <Button size="1" variant="soft" disabled={effectivePage >= totalPages || loading}
            onClick={() => setPage(effectivePage + 1)}>Next</Button>
        </Flex>
      )}
    </Flex>
  );
});

CustomPatientListView.displayName = 'CustomPatientListView';

export default CustomPatientListView;
